import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import {
  CategoryField,
  CategoryChoiceValue,
  CategoryChoice,
  CategoryChoiceLongHint,
  TextCollectionFieldType,
} from '@scale/shared/types/params/textcollection';
import {
  CategoryFieldAnnotation,
  TextCollectionAnnotation,
} from '@scale/shared/types/response/textcollection';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { FormHelperText, IconButton, Button, useMediaQuery } from '@material-ui/core';
import {
  getSectionTitle,
  getHelperText,
  getHelperDescriptionForMinMaxSelections,
} from './helperText';
import {
  getFieldMaxResponses,
  getFieldMinResponses,
  addDefaultResponse,
} from '@scale/shared/textcollection/defaults';
import classnames from 'classnames';
import { makeStyles } from 'design-system/styles';
import { Checkbox } from 'design-system';
import { Radio } from 'design-system/Radio';
import { RadioGroup } from 'design-system/RadioGroup';
import TextInput from 'design-system/TextInput';
// @ts-ignore JS package
import pluralize from 'pluralize';
import HintTooltipIcon from './HintTooltipIcon';
import { TextCollectionSuggestionValue } from '@scale/shared/types/textCollection';
import {
  depthFirstChoices,
  getCategoryParentValues,
  getCategoryFullLabelPath,
  getCategoryFullPath,
  getChoiceFromValue,
} from '@scale/shared/textcollection/field';
import { useSuggestions } from './suggestions';
import Fuse from 'fuse.js';
import {
  getCategoryAnnotationChecked,
  getCategoryAnnotationCheckedGroupLabels,
} from 'react-scale/components/scalers/tasks/textcollection/FieldPreview';
import fetch from 'isomorphic-fetch';
import {
  ChoiceLongHint,
  ChoiceLongHintAndWorkflow,
  ChoiceWorkflow,
} from './CategoryFieldLongHints';
import { MOBILE_THRESHOLD } from '@scale/frontend-lib/util';
import { Event, logEvent } from 'react-scale/lib/events';
import {
  areFieldConditionsMet,
  cosineSimilarity,
  isInVocabulary,
  embedString,
  fetchLongHints,
} from '@scale/shared/textcollection/helpers';
import { CategoryChooseMultipleAcceptableAudit } from 'react-scale/components/scalers/tasks/textcollection/CategoryChooseMultipleAcceptableAudit';
import { TextCollectionConfig, DOLPHIN_MISC } from '@scale/shared/defaults';
import {
  ALPHABETIC_SHORTCUTS,
  useSequentialAlphabeticShortcuts,
} from 'react-scale/hooks/useSequentialShortcuts';
import Kbd from 'design-system/Kbd';
import apiFetch from 'react-scale/lib/api-fetch';
import { useUnleashBoolean } from 'react-scale/hooks/useUnleashFeatureFlag';
import { useLLMHelper } from 'react-scale/dashboard.scale.com/contexts/LLMHelperContext';
import OpenRubricButton from './OpenRubricButton';
import { includesValueTrimmingSpaces } from './utils';

interface CategorizationFieldSectionProps {
  field: CategoryField;
  annotation: CategoryFieldAnnotation;
  currentAnnotations: Record<string, TextCollectionAnnotation>;
  suggestions?: TextCollectionSuggestionValue[];
  setAnnotation: (annotation: CategoryFieldAnnotation) => void;
  disabled?: boolean;
  assignmentId?: string;
  loggedUserId?: string;
  textCollectionConfig?: TextCollectionConfig;
  isAudit?: boolean;
  isFocused?: boolean; // this is only true if we can use shortcuts
  dolphinSetEnhancedResponse?: {
    setEnhancedResponseFn: (annotation: TextCollectionAnnotation) => void;
    generatedResponseA: TextCollectionAnnotation;
    generatedResponseB: TextCollectionAnnotation;
    hasEnhancedResponseField: boolean;
  };
  projectId?: string;
  hideTitle?: boolean;
  useKeyboardShortcuts?: boolean;
}

// eslint-disable-next-line complexity
export default function CategoryFieldSection(props: CategorizationFieldSectionProps) {
  const styles = useCategorizationFieldSectionStyles();

  const addNewResponse = () => {
    props.setAnnotation(
      addDefaultResponse(props.annotation, props.field) as CategoryFieldAnnotation,
    );
  };

  const onChange = function (index: number, values: CategoryChoiceValue[]): void {
    const response = props.annotation.response.slice();
    response[index] = values;
    const annotation: CategoryFieldAnnotation = {
      ...props.annotation,
      response,
    };

    if (
      props.dolphinSetEnhancedResponse &&
      props.dolphinSetEnhancedResponse.hasEnhancedResponseField
    ) {
      const enhancedFieldResponse = (
        response[0][0] === DOLPHIN_MISC.DOLPHIN_GENERATED_FIELD_A
          ? [props.dolphinSetEnhancedResponse.generatedResponseA.response[0]]
          : [props.dolphinSetEnhancedResponse.generatedResponseB.response[0]]
      ) as string[];

      props.dolphinSetEnhancedResponse.setEnhancedResponseFn({
        type: TextCollectionFieldType.Text,
        field_id: DOLPHIN_MISC.DOLPHIN_ENHANCED_FIELD,
        response: enhancedFieldResponse,
      });
    }

    props.setAnnotation(annotation);
  };

  const unleashUseOntologyHints = useUnleashBoolean(
    'use-ontology-hints',
    { projectId: props.projectId },
    {
      shortCircuit: !props.projectId,
      defaultValue: false,
    },
  );

  // Stored in s3 for faster updates, and to alleviate mongo burden
  const [fieldLongHints, setFieldLongHints] = React.useState<
    Record<string, CategoryChoiceLongHint[]>
  >({});
  React.useEffect(() => {
    setFieldLongHints({});
    const fetchLongHintsFromOntology = async () => {
      try {
        const { longHints } = await apiFetch(
          null,
          `/internal/ontology_manager/get_long_hints_from_ontology/${props.projectId}`,
          {
            method: 'GET',
          },
        );
        setFieldLongHints(longHints);
      } catch (error) {
        return;
      }
    };
    if (props.field.useOntologyHints || unleashUseOntologyHints) {
      fetchLongHintsFromOntology();
    } else if (props.field.longHintsS3Artifact) {
      fetchLongHints(props.field.longHintsS3Artifact, setFieldLongHints);
    }
  }, [props.field, props.projectId, unleashUseOntologyHints]);

  const [field, setField] = React.useState<CategoryField>(props.field);

  React.useEffect(() => {
    setField(props.field);
    if (!props.field.choicesS3Artifact) {
      return;
    }
    const fetchChoicesFromS3 = async () => {
      try {
        const resp = await fetch(props.field.choicesS3Artifact!);
        const respText = await resp.text();
        const choices = JSON.parse(respText);
        const newField = { ...field, choices };

        setField(newField);
      } catch (error) {
        return;
      }
    };
    fetchChoicesFromS3();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.field]);

  const [embeddings, setEmbeddings] = React.useState<Record<string, number[]>>({});
  const [embeddingDim, setEmbeddingDim] = React.useState<number>(0);

  const useSemanticSearch = props.textCollectionConfig?.useSemanticSearch;

  const useCategoryChooseMultipleAcceptableAudit =
    props.textCollectionConfig?.acceptableAuditWorkflow;

  React.useEffect(() => {
    setEmbeddings({});
    if (!useSemanticSearch) {
      return;
    }

    const fetchEmbeddingsFromS3 = async () => {
      try {
        if (props.textCollectionConfig?.embeddingsLink) {
          const resp = await fetch(props.textCollectionConfig.embeddingsLink ?? '');
          const respText = await resp.text();
          const embeddingsJson = JSON.parse(respText);
          setEmbeddings(embeddingsJson);

          const keys = Object.keys(embeddingsJson);
          setEmbeddingDim(embeddingsJson[keys[0]].length);
        }
      } catch (error) {
        return;
      }
    };
    fetchEmbeddingsFromS3();
  }, [props.textCollectionConfig?.embeddingsLink, useSemanticSearch]);

  const onRemove = (index: number) => {
    const response = props.annotation.response.slice();
    response.splice(index, 1);
    const annotation: CategoryFieldAnnotation = {
      ...props.annotation,
      response,
    };
    props.setAnnotation(annotation);
  };

  const { getFieldSuggestionsHint } = useSuggestions();
  const suggestionsSet = React.useMemo(() => {
    return new Set(_.flatMap(props.suggestions, suggestion => suggestion.value));
  }, [props.suggestions]);
  const suggestionsHint = React.useMemo(
    () => getFieldSuggestionsHint(props.field),
    [props.field, getFieldSuggestionsHint],
  );

  if (!props.annotation.response) props.annotation.response = [];
  const responses = props.annotation.response.length;
  const minResponses = getFieldMinResponses(props.field);
  const maxResponses = getFieldMaxResponses(props.field);
  const isMultipleAnnotations = maxResponses > 1;
  // HACK: want to use CategoryChooseMultiple when having a single select in order to allow for 0 or 1 selection behavior
  // the logic assumes that min_choices defaults to 1 (which isn't actually true) for single select cases
  const isMultipleChoice =
    (props.field.max_choices || 0) > 1 || props.field.use_category_choose_multiple;
  const llmHelper = useLLMHelper();
  const [aiAssistant, setAiAssistant] = useState<string>('');
  const [aiAssistantLoading, setAiAssistantLoading] = useState<boolean>(false);

  useEffect(() => {
    if (
      llmHelper.aiAssistantEnabled &&
      Object.keys(llmHelper.aiAssistantPrompts).includes(props.field.field_id)
    ) {
      (async () => {
        try {
          setAiAssistantLoading(true);
          const content = await llmHelper.getAiAssistantResponse(props.field.field_id);
          if (content) setAiAssistant(content);
        } finally {
          setAiAssistantLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmHelper.aiAssistantEnabled, llmHelper.aiAssistantPrompts, props.field.field_id]);

  const categoryFields = props.annotation.response.map(
    (response: CategoryChoiceValue[], index: number) => {
      const isFirst = index === 0;
      const isLast = index === props.annotation.response.length - 1;
      return (
        <div
          key={`${props.field.field_id} ${index}`}
          className={classnames({
            [styles.multiple]: isMultipleAnnotations,
            [styles.first]: isFirst,
            [styles.last]: isLast,
          })}
        >
          {(!!aiAssistant.length || aiAssistantLoading) && (
            <div
              style={{
                marginBottom: 8,
                padding: 8,
                whiteSpace: 'pre-wrap',
                backgroundColor: 'rgba(144, 202, 249, .2)',
                borderRadius: 8,
              }}
            >
              {aiAssistantLoading && !aiAssistant.length
                ? 'AI assistant evaluation is loading...'
                : `The AI assistant suggests: ${aiAssistant}`}
            </div>
          )}
          {!isMultipleChoice && (
            <CategoryChooseSingle
              response={response}
              field={field}
              updateResponse={newValues => onChange(index, newValues)}
              suggestionsSet={suggestionsSet as unknown as Set<CategoryChoiceValue>}
              suggestionsHint={suggestionsHint}
              disabled={props.disabled}
              fieldLongHints={fieldLongHints}
              useSemanticSearch={useSemanticSearch}
              wordEmbeddings={embeddings}
              embeddingDim={embeddingDim}
              textCollectionConfig={props.textCollectionConfig}
              isFocused={props.isFocused}
              currentAnnotations={props.currentAnnotations}
              useKeyboardShortcuts={props.useKeyboardShortcuts}
            />
          )}
          {isMultipleChoice && (!useCategoryChooseMultipleAcceptableAudit || !props.isAudit) && (
            <CategoryChooseMultiple
              response={response}
              field={field}
              updateResponse={newValues => onChange(index, newValues)}
              suggestionsSet={suggestionsSet as unknown as Set<CategoryChoiceValue>}
              suggestionsHint={suggestionsHint}
              fieldLongHints={fieldLongHints}
              disabled={props.disabled}
              assignmentId={props.assignmentId}
              loggedUserId={props.loggedUserId}
              useSemanticSearch={useSemanticSearch}
              wordEmbeddings={embeddings}
              embeddingDim={embeddingDim}
              textCollectionConfig={props.textCollectionConfig}
              isFocused={props.isFocused}
              currentAnnotations={props.currentAnnotations}
              useKeyboardShortcuts={props.useKeyboardShortcuts}
            />
          )}
          {isMultipleChoice && useCategoryChooseMultipleAcceptableAudit && props.isAudit && (
            <CategoryChooseMultipleAcceptableAudit
              response={response}
              field={field}
              updateResponse={newValues => onChange(index, newValues)}
              suggestionsSet={suggestionsSet as unknown as Set<CategoryChoiceValue>}
              suggestionsHint={suggestionsHint}
              fieldLongHints={fieldLongHints}
              disabled={props.disabled}
              assignmentId={props.assignmentId}
              loggedUserId={props.loggedUserId}
              useSemanticSearch={useSemanticSearch}
              wordEmbeddings={embeddings}
              embeddingDim={embeddingDim}
              textCollectionConfig={props.textCollectionConfig}
              currentAnnotations={props.currentAnnotations}
            />
          )}
          {isMultipleAnnotations && !props.disabled && (
            <div className={styles.actions}>
              <IconButton
                component="div"
                classes={{ root: styles.delete }}
                onClick={() => onRemove(index)}
                disabled={index < minResponses}
              >
                <TrashIcon />
              </IconButton>
            </div>
          )}
        </div>
      );
    },
  );

  const shouldPluralize = maxResponses > 1;
  const title = props.field.title || props.field.field_id;
  const requiredStr = props.field.required ? ' *' : '';
  let titleString = shouldPluralize
    ? pluralize(title, props.field.max_responses_required || 1, false)
    : title;
  titleString = `${titleString}${requiredStr}`;
  let hint = props.field.hint || props.field.instruction;
  if (props.field.showParentLonghint && !hint) {
    hint = fieldLongHints[props.field.field_id]?.[0]?.text;
  }
  const minMaxText = getHelperText({
    description:
      isMultipleChoice && props.field.hideMinMaxLabel !== true
        ? getHelperDescriptionForMinMaxSelections(props.field.min_choices, props.field.max_choices)
        : undefined,
  });
  const helperText = getHelperText({ description: props.field.description });
  return (
    <>
      {!props.hideTitle && (
        <div className={styles.title}>
          <div className="flex">
            {getSectionTitle(titleString)}
            {hint && <HintTooltipIcon hint={hint} classes={{ button: styles.tooltipButton }} />}
          </div>
          {props.field.rubric && <OpenRubricButton rubric={props.field.rubric} />}
        </div>
      )}
      {minMaxText && !props.hideTitle && (
        <FormHelperText classes={{ root: styles.minMaxText }}>{minMaxText}</FormHelperText>
      )}
      {helperText && !props.hideTitle && (
        <FormHelperText classes={{ root: styles.helperText }}>{helperText}</FormHelperText>
      )}
      {categoryFields}
      {responses < maxResponses && (
        <div style={{ display: props.disabled ? 'none' : 'block' }}>
          <Button
            type="button"
            classes={{ root: styles.addButton, label: styles.addButtonText }}
            startIcon={<PlusIcon />}
            color="primary"
            variant="text"
            onClick={addNewResponse}
          >
            Add {title}
          </Button>
        </div>
      )}
    </>
  );
}

const CategoryChooseSingle: React.FC<{
  field: CategoryField;
  updateResponse: (answer: CategoryChoiceValue[]) => void;
  response: CategoryChoiceValue[];
  suggestionsSet: Set<CategoryChoiceValue>;
  suggestionsHint?: string;
  disabled?: boolean;
  fieldLongHints?: Record<string, CategoryChoiceLongHint[]>;
  useSemanticSearch?: boolean;
  wordEmbeddings?: Record<string, number[]>;
  embeddingDim?: number;
  textCollectionConfig?: TextCollectionConfig;
  isFocused?: boolean;
  currentAnnotations: Record<string, TextCollectionAnnotation>;
  useKeyboardShortcuts?: boolean;
}> = ({
  field,
  updateResponse,
  response,
  suggestionsSet,
  suggestionsHint,
  disabled,
  fieldLongHints,
  useSemanticSearch,
  wordEmbeddings,
  embeddingDim,
  textCollectionConfig,
  isFocused,
  currentAnnotations,
  useKeyboardShortcuts,
}) => {
  const classes = useCategorizationFieldSectionStyles();

  const autoComplete = useCategoryAutoComplete(
    field,
    false,
    fieldLongHints,
    useSemanticSearch,
    wordEmbeddings,
    embeddingDim,
  );

  const [applyInitialChoiceFilter, setApplyInitialChoiceFilter] = React.useState<boolean>(
    !_.isEmpty(field.initialChoiceFilter),
  );

  const [visible, setVisible] = React.useState<CategoryChoiceValue[]>(() => {
    return getCategoryParentValues(field.choices, response);
  });

  const initialChoiceFilter = _.uniq(field.initialChoiceFilter);

  const hasInitialChoiceFilter = !_.isNil(initialChoiceFilter) && !_.isEmpty(initialChoiceFilter);

  const mobile = useMediaQuery(`(max-width: ${MOBILE_THRESHOLD})`);

  // Disables auto-selecting the parent when un-collapsing the children
  const disableIntermediateAutoSelect = textCollectionConfig?.disableIntermediateAutoSelect;

  React.useEffect(() => {
    if (applyInitialChoiceFilter && hasInitialChoiceFilter) {
      setVisible(initialChoiceFilter!);
    } else {
      setVisible(getCategoryParentValues(field.choices, response));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field]);

  const [longHints, setLongHints] = React.useState<CategoryChoiceLongHint[]>([]);
  const [previousLongHints, setPreviousLongHints] = React.useState<CategoryChoiceLongHint[]>([]);
  const [childrenLongHints, setChildrenLongHints] = React.useState<CategoryChoiceLongHint[]>([]);
  const [workflows, setWorkFlows] = React.useState<string[]>([]);
  const [mobileHintsSeen, setMobileHintsSeen] = React.useState<Set<string | undefined>>(new Set());

  const addToMobileHintsSeen = (hints: (string | undefined)[]) => {
    setMobileHintsSeen(new Set([...mobileHintsSeen, ...hints]));
  };

  const findAndSetLongHints = (nextVisible: CategoryChoiceValue[]) => {
    if (!_.isEmpty(nextVisible)) {
      if (!_.isNil(fieldLongHints)) {
        let hints = _.compact(
          _.flatten(nextVisible.map(value => fieldLongHints[value.toString()])),
        );

        const selectedWorkflows = _.compact(hints.map(hint => hint.workflow));
        setWorkFlows(selectedWorkflows);

        if (mobile) {
          hints = hints.filter(h => !mobileHintsSeen.has(h.title));
        }

        const childrenChoices = getChoiceFromValue(
          field.choices,
          nextVisible[0].toString(),
        )?.subchoices;
        if (childrenChoices) {
          const childrenHints = _.compact(
            _.flatten(childrenChoices.map(choice => fieldLongHints[choice.value.toString()])),
          );
          setChildrenLongHints(childrenHints);
        }

        setPreviousLongHints(longHints);
        setLongHints(hints);
        addToMobileHintsSeen(hints.map(h => h.title));
      }
    } else {
      setLongHints([]);
      setWorkFlows([]);
    }
  };

  const onLeafChange = (value: string) => {
    updateResponse([value]);
    const nextVisible = getCategoryParentValues(field.choices, [value]).concat([value]);
    setVisible(nextVisible);
    findAndSetLongHints(nextVisible);
    autoComplete.clear();
  };

  const canSelectIntermediateNodesFieldFlag = field.can_select_intermediate_nodes;
  const canSelectIntermediateNodesConfig = textCollectionConfig?.canSelectIntermediateNodes;
  const canSelectIntermediateNodes =
    canSelectIntermediateNodesFieldFlag || canSelectIntermediateNodesConfig;

  const renderChoice = (
    choice: CategoryChoice,
    parentChoices: CategoryChoice[] = [],
    shortcut?: string,
  ): React.ReactNode => {
    if (choice.conditions && !areFieldConditionsMet(choice.conditions, currentAnnotations)) {
      return;
    }
    const key = `${field.field_id} ${choice.value}`;
    const label = (
      <ChoiceLabel
        label={choice.label}
        hint={choice.hint || choice.instruction}
        subLabel={choice.subLabel}
        preferred={choice.preferred}
        link={choice.link}
      />
    );

    const getLeafLabel = (isSuggested: boolean) => (
      <div className={classes.checkboxLabel}>
        {label}
        {isSuggested ? (
          <div className={classes.suggestedText}>
            Suggested
            {suggestionsHint && <HintTooltipIcon hint={suggestionsHint} />}
          </div>
        ) : null}
      </div>
    );

    if (!isParentChoice(choice)) {
      const isSuggested = suggestionsSet.has(choice.value);
      const leafLabel = getLeafLabel(isSuggested);
      return (
        <div style={{ display: 'flex' }} key={key}>
          {shortcut && (
            <div className={classes.shortcut}>
              <Kbd>{shortcut}</Kbd>
            </div>
          )}
          <Radio
            className={classnames(classes.choicesRow, {
              [classes.suggested]: isSuggested,
              [classes.highlightNode]: includesValueTrimmingSpaces(response, choice.value),
              [classes.noPaddingLeft]: !!shortcut,
            })}
            value={choice.value.toString()}
            labelPlacement="end"
            label={leafLabel}
            disabled={disabled}
            radioColor={includesValueTrimmingSpaces(response, choice.value) ? '#6A5DF9' : undefined}
          />
        </div>
      );
    }

    const isVisible = visible.includes(choice.value);

    const onParentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextVisible = new Set<CategoryChoiceValue>();
      for (const parentChoice of parentChoices) {
        nextVisible.add(parentChoice.value);
      }
      if (event.target.checked) {
        nextVisible.add(choice.value);
      }
      if (canSelectIntermediateNodes && !disableIntermediateAutoSelect) {
        onLeafChange(choice.value.toString());
      } else {
        updateResponse([]);
        setWorkFlows([]);
      }
      setVisible(Array.from(nextVisible));
      findAndSetLongHints(Array.from(nextVisible));
    };

    let parentLabel = label;
    if (canSelectIntermediateNodes) {
      // Add radio button to indicate parent is also selectable
      const isSuggested = suggestionsSet.has(choice.value);
      const leafLabel = getLeafLabel(isSuggested);
      parentLabel = (
        <Radio
          className={classnames(classes.parentChoicesRow, {
            [classes.suggested]: isSuggested,
          })}
          radioClassName={classes.parentRadio}
          value={choice.value.toString()}
          labelPlacement="end"
          label={leafLabel}
          disabled={disabled}
        />
      );
    }

    return (
      <React.Fragment key={key}>
        <ParentCheckbox
          checked={isVisible}
          label={parentLabel}
          onChange={onParentChange}
          disabled={disabled}
          canSelectIntermediateNodes={canSelectIntermediateNodes}
          highlightNode={isVisible || includesValueTrimmingSpaces(response, choice.value)}
          shortcut={isFocused ? shortcut : undefined}
        />
        {isVisible && _.isArray(choice.subchoices) && (
          <div className={classes.subchoices}>
            {choice.subchoices.map(subchoice =>
              renderChoice(subchoice, [...parentChoices, choice]),
            )}
          </div>
        )}
      </React.Fragment>
    );
  };

  const hasNestedOptions = _.some(field.choices, isParentChoice);
  const searchEnabled = (field.showSearchBar || hasNestedOptions) && !disabled;
  const showSelectedChoices = hasNestedOptions || field.showSearchBar;
  const showChoices = !field.showSearchBar || autoComplete.isActive;
  const anchorRef = React.useRef(null);

  const filterOutEmptyChoices = React.useCallback((choices: CategoryChoice[]) => {
    const filteredChoices = [];
    for (const choice of choices) {
      if (choice.subchoices) {
        choice.subchoices = filterOutEmptyChoices(choice.subchoices);
      }
      if (choice.label.length) {
        filteredChoices.push(choice);
      }
    }
    return filteredChoices;
  }, []);

  const maybeFilterChoices = React.useCallback(
    (unfilteredChoices: CategoryChoice[]): CategoryChoice[] => {
      if (applyInitialChoiceFilter && hasInitialChoiceFilter) {
        return _.compact(
          initialChoiceFilter!.map(choiceValue =>
            getChoiceFromValue(unfilteredChoices, choiceValue),
          ),
        );
      }
      return unfilteredChoices;
    },
    [applyInitialChoiceFilter, hasInitialChoiceFilter, initialChoiceFilter],
  );

  const handleCloseWorkflow = (isWorkflowComplete: boolean) => {
    if (!isWorkflowComplete) {
      updateResponse([]);
    }
    setWorkFlows([]);
  };

  const handleBothCloseWorkflow = (isWorkflowComplete: boolean) => {
    if (!isWorkflowComplete) {
      updateResponse([]);
    }
    setWorkFlows([]);
    setLongHints([]);
  };

  const filteredChoices = React.useMemo(() => {
    return maybeFilterChoices(filterOutEmptyChoices(autoComplete.choices));
  }, [autoComplete.choices, maybeFilterChoices, filterOutEmptyChoices]);

  useSequentialAlphabeticShortcuts((idx: number) => {
    if (
      filteredChoices[idx]?.value &&
      (!isParentChoice(autoComplete.choices[idx]) || canSelectIntermediateNodes)
    ) {
      onLeafChange(filteredChoices[idx].value.toString());
    }
  }, !isFocused || !showChoices);

  return (
    <div className={classes.choicesContainer} ref={anchorRef}>
      {showSelectedChoices && (
        <div className={classes.preview}>
          {'Choice Path: '}
          <span className={classes.responsePath}>
            {getCategoryFullLabelPath(response[0], field)}
          </span>
        </div>
      )}

      {mobile && (
        <ChoiceLongHintAndWorkflow
          longHints={longHints}
          isOpen={!_.isEmpty(longHints) || (!_.isEmpty(workflows) && !_.isEmpty(response))}
          anchorRef={anchorRef}
          choice={getChoiceFromValue(field.choices, response[0])}
          workflows={workflows}
          onClose={handleBothCloseWorkflow}
        />
      )}

      {!mobile && (
        <>
          <ChoiceLongHint
            longHints={longHints}
            isOpen={!_.isEmpty(longHints)}
            anchorRef={anchorRef}
            previousLongHints={previousLongHints}
            childrenLongHints={childrenLongHints}
            setLongHints={setLongHints}
            setPreviousLongHints={setPreviousLongHints}
            setChildrenLongHints={setChildrenLongHints}
          />
          {!_.isEmpty(workflows) && !_.isEmpty(response) && (
            <ChoiceWorkflow
              choice={getChoiceFromValue(field.choices, response[0])}
              anchorRef={anchorRef}
              workflows={workflows}
              onClose={handleCloseWorkflow}
            />
          )}
        </>
      )}

      {searchEnabled && (
        <div className={classes.autoCompleteRow}>
          <TextInput
            value={autoComplete.pattern}
            onChange={event => autoComplete.search(event.target.value)}
            fullWidth
            placeholder="Search choices..."
            onKeyDown={e => {
              if ((e.key === 'Escape' || e.key === 'Tab') && e.target instanceof HTMLElement) {
                e.target.blur();
              }
              e.stopPropagation();
            }}
          />
        </div>
      )}
      {!_.isEmpty(field.initialChoiceFilter) && (
        <Button
          className={classes.filterOptions}
          onClick={() => setApplyInitialChoiceFilter(!applyInitialChoiceFilter)}
          color="primary"
          variant="text"
          size="small"
        >
          {applyInitialChoiceFilter ? 'View All' : 'View Filtered'}
        </Button>
      )}
      {showChoices && (
        <RadioGroup value={response[0]?.toString()} onChange={onLeafChange}>
          {filteredChoices.map((choice, idx) =>
            renderChoice(
              choice,
              undefined,
              isFocused && useKeyboardShortcuts ? ALPHABETIC_SHORTCUTS[idx] : undefined,
            ),
          )}
        </RadioGroup>
      )}
    </div>
  );
};

export const CategoryChooseMultiple: React.FC<{
  field: CategoryField;
  updateResponse: (answer: CategoryChoiceValue[]) => void;
  response: CategoryChoiceValue[];
  suggestionsSet: Set<CategoryChoiceValue>;
  suggestionsHint?: string;
  disabled?: boolean;
  fieldLongHints?: Record<string, CategoryChoiceLongHint[]>;
  assignmentId?: string;
  loggedUserId?: string;
  useSemanticSearch?: boolean;
  wordEmbeddings?: Record<string, number[]>;
  embeddingDim?: number;
  disabledResponse?: CategoryChoiceValue[];
  textCollectionConfig?: TextCollectionConfig;
  isFocused?: boolean;
  currentAnnotations: Record<string, TextCollectionAnnotation>;
  useKeyboardShortcuts?: boolean;
}> = ({
  field,
  updateResponse,
  response,
  suggestionsSet,
  suggestionsHint,
  disabled,
  fieldLongHints,
  assignmentId,
  loggedUserId,
  useSemanticSearch,
  wordEmbeddings,
  embeddingDim,
  disabledResponse = [],
  textCollectionConfig,
  isFocused,
  currentAnnotations,
  useKeyboardShortcuts,
}) => {
  const classes = useCategorizationFieldSectionStyles();

  const mobile = useMediaQuery(`(max-width: ${MOBILE_THRESHOLD})`);

  const persistSearchQueryAfterSelect = textCollectionConfig?.persistSearchQueryAfterSelect;

  const [visible, setVisible] = React.useState<CategoryChoiceValue[]>(() => {
    return getCategoryParentValues(field.choices, response);
  });

  const [searchQueryText, setSearchQueryText] = React.useState<string>('');
  const [previousLongHints, setPreviousLongHints] = React.useState<CategoryChoiceLongHint[]>([]);
  const [childrenLongHints, setChildrenLongHints] = React.useState<CategoryChoiceLongHint[]>([]);
  const [longHints, setLongHints] = React.useState<CategoryChoiceLongHint[]>([]);
  const [workflows, setWorkFlows] = React.useState<string[]>([]);
  const [choiceValueForWorkflow, setChoiceValueForWorkflow] = React.useState<null | string>(null);
  const [mobileHintsSeen, setMobileHintsSeen] = React.useState<Set<string | undefined>>(new Set());

  const addToMobileHintsSeen = (hints: (string | undefined)[]) => {
    setMobileHintsSeen(new Set([...mobileHintsSeen, ...hints]));
  };

  const autoComplete = useCategoryAutoComplete(
    field,
    false,
    fieldLongHints,
    useSemanticSearch,
    wordEmbeddings,
    embeddingDim,
  );

  // TODO compute embeddings & initialize search function here

  const responseSetToNodesInPath = (response: Set<CategoryChoiceValue>): string[] => {
    const newNodesInPath: string[] = [];
    response.forEach(choice =>
      newNodesInPath.push(...getCategoryFullPath(field.choices, choice).values),
    );
    return newNodesInPath;
  };

  const [nodesInPath, setNodesInPath] = React.useState<string[]>(() => {
    return responseSetToNodesInPath(new Set(response));
  });

  const groupSameLabelChoicesFieldFlag = field.groupSamePathLabelCategories;
  const groupSameLabelChoicesConfig = textCollectionConfig?.groupSameLabelChoices;
  const groupSameLabelChoices = groupSameLabelChoicesFieldFlag || groupSameLabelChoicesConfig;

  const styleSelectedChoices = textCollectionConfig?.styleSelectedChoicesBorders;

  const categoryPathLabelGroups: Record<string, CategoryChoiceValue[]> = React.useMemo(() => {
    const allChoices: CategoryChoice[] = [];
    const allCategoryPathLabelGroups: Record<string, CategoryChoiceValue[]> = {};
    if (!groupSameLabelChoices) return allCategoryPathLabelGroups;
    for (const [choice] of depthFirstChoices(field.choices)) {
      allChoices.push(choice);
    }
    allChoices.forEach(choice => {
      allCategoryPathLabelGroups[choice.label] = [
        ...(allCategoryPathLabelGroups[choice.label] ?? []),
        choice.value,
      ];
    });
    return allCategoryPathLabelGroups;
  }, [field, groupSameLabelChoices]);

  const findAndSetLongHints = (value: CategoryChoiceValue | undefined) => {
    if (value) {
      if (!_.isNil(fieldLongHints)) {
        let hints = _.compact(fieldLongHints[value.toString()]);
        const selectedWorkflows = _.compact(hints.map(hint => hint.workflow));

        setWorkFlows(selectedWorkflows);

        if (mobile) {
          hints = hints.filter(h => !mobileHintsSeen.has(h.title));
        }

        const childrenChoices = getChoiceFromValue(field.choices, value.toString())?.subchoices;
        if (childrenChoices) {
          const childrenHints = _.compact(
            _.flatten(childrenChoices.map(choice => fieldLongHints[choice.value.toString()])),
          );
          setChildrenLongHints(childrenHints);
        }

        setChoiceValueForWorkflow(value.toString());
        setPreviousLongHints(longHints);
        setLongHints(hints);
        addToMobileHintsSeen(hints.map(h => h.title));
      }
    } else {
      setLongHints([]);
      setChoiceValueForWorkflow(null);
      setWorkFlows([]);
    }
  };

  const onLeafChange = async (choice: CategoryChoice, leafChecked: boolean) => {
    const newResponse = new Set(response);
    const allRelevantChoiceValues = groupSameLabelChoices
      ? categoryPathLabelGroups[choice.label]
      : [choice.value];
    if (leafChecked) {
      // Unset relevant choices' parents
      allRelevantChoiceValues.forEach(async choiceValue => {
        const fullPathChoiceValues = getCategoryFullPath(field.choices, choiceValue).values;
        for (const parentChoiceValue of fullPathChoiceValues.slice(0, -1)) {
          newResponse.delete(parentChoiceValue);
        }
        newResponse.add(choiceValue);

        if (searchEnabled) {
          const categorySearchEvent = {
            assignmentId,
            loggedUserId,
            searchQuery: autoComplete.pattern,
            selectedCategory: choiceValue,
            isFuseMatch: choice.isFuseMatch,
          };
          await logEvent(Event.CategorySearchEvent, categorySearchEvent);
        }
      });
    } else {
      allRelevantChoiceValues.forEach(choiceValue => newResponse.delete(choiceValue));
    }
    findAndSetLongHints(leafChecked ? choice.value : undefined);
    updateResponse([...newResponse]);
    setNodesInPath(responseSetToNodesInPath(newResponse));
    if (!persistSearchQueryAfterSelect) {
      autoComplete.clear();
      setSearchQueryText('');
    }
  };

  const canSelectIntermediateNodesFieldFlag = field.can_select_intermediate_nodes;
  const canSelectIntermediateNodesConfig = textCollectionConfig?.canSelectIntermediateNodes;
  const canSelectIntermediateNodes =
    canSelectIntermediateNodesFieldFlag || canSelectIntermediateNodesConfig;

  const renderChoice = (choice: CategoryChoice, shortcut?: string): React.ReactNode => {
    if (choice.conditions && !areFieldConditionsMet(choice.conditions, currentAnnotations)) {
      return;
    }
    const key = `${field.field_id} ${choice.value}`;
    const label = (
      <ChoiceLabel
        label={choice.label}
        hint={choice.hint || choice.instruction}
        subLabel={choice.subLabel}
        preferred={choice.preferred}
        link={choice.link}
      />
    );

    const getLeafLabel = (isSuggested: boolean) => (
      <div className={classes.checkboxLabel}>
        {label}
        {isSuggested ? (
          <div className={classes.suggestedText}>
            Suggested
            {suggestionsHint && <HintTooltipIcon hint={suggestionsHint} />}
          </div>
        ) : null}
      </div>
    );

    if (!isParentChoice(choice)) {
      const isSuggested = suggestionsSet.has(choice.value);
      const leafLabel = getLeafLabel(isSuggested);
      return (
        <div style={{ display: 'flex' }} key={key}>
          {shortcut && (
            <div className={classes.shortcut}>
              <Kbd>{shortcut}</Kbd>
            </div>
          )}
          <Checkbox
            className={classnames(classes.choicesRow, {
              [classes.suggested]: isSuggested,
              [classes.highlightNode]: nodesInPath.includes(choice.value as string),
              [classes.noPaddingLeft]: shortcut,
            })}
            classes={{
              label: classes.checkboxLabel,
            }}
            checked={
              includesValueTrimmingSpaces(response, choice.value) ||
              includesValueTrimmingSpaces(disabledResponse, choice.value)
            }
            labelPlacement="end"
            label={leafLabel}
            onChange={e => onLeafChange(choice, e.target.checked)}
            disabled={disabled || includesValueTrimmingSpaces(disabledResponse, choice.value)}
            checkBoxColor="rgba(106, 93, 249)"
          />
        </div>
      );
    }
    const isVisible = visible.includes(choice.value);
    const onParentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextVisible = new Set(visible);
      if (event.target.checked) {
        nextVisible.add(choice.value);
      } else {
        nextVisible.delete(choice.value);
      }
      setWorkFlows([]);
      setChoiceValueForWorkflow(null);
      setVisible(Array.from(nextVisible));
    };

    let parentLabel = label;
    if (canSelectIntermediateNodes) {
      // Add checkbox to indicate parent is also selectable
      const isSuggested = suggestionsSet.has(choice.value);
      const leafLabel = getLeafLabel(isSuggested);
      parentLabel = (
        <div style={{ display: 'flex' }}>
          <Checkbox
            key={key}
            className={classnames(classes.parentChoicesRow, {
              [classes.suggested]: isSuggested,
              [classes.noPaddingLeft]: !!shortcut,
            })}
            classes={{
              label: classes.checkboxLabel,
            }}
            checked={
              includesValueTrimmingSpaces(response, choice.value) ||
              includesValueTrimmingSpaces(disabledResponse, choice.value)
            }
            labelPlacement="end"
            label={leafLabel}
            onChange={e => onLeafChange(choice, e.target.checked)}
            disabled={disabled || includesValueTrimmingSpaces(disabledResponse, choice.value)}
            checkBoxColor="rgba(106, 93, 249)"
          />
        </div>
      );
    }

    return (
      <React.Fragment key={key}>
        <ParentCheckbox
          checked={isVisible}
          label={parentLabel}
          onChange={onParentChange}
          disabled={disabled}
          canSelectIntermediateNodes={canSelectIntermediateNodes}
          highlightNode={nodesInPath.includes(choice.value as string)}
          shortcut={isFocused ? shortcut : undefined}
        />
        {isVisible && _.isArray(choice.subchoices) && (
          <div className={classes.subchoices}>
            {choice.subchoices.map(choice => renderChoice(choice))}
          </div>
        )}
      </React.Fragment>
    );
  };

  const hasNestedOptions = _.some(field.choices, isParentChoice);
  const searchEnabled = (field.showSearchBar || hasNestedOptions) && !disabled;
  const showSelectedChoices = hasNestedOptions || field.showSearchBar || styleSelectedChoices;
  const showChoices = !field.showSearchBar || autoComplete.isActive;
  const anchorRef = React.useRef(null);

  useSequentialAlphabeticShortcuts(
    (idx: number) => {
      if (
        autoComplete.choices[idx] &&
        (!isParentChoice(autoComplete.choices[idx]) || canSelectIntermediateNodes)
      ) {
        const newResponse = new Set(response);
        onLeafChange(autoComplete.choices[idx], !newResponse.has(autoComplete.choices[idx].value));
      }
    },
    !isFocused || !showChoices,
    undefined,
    true,
  );

  const handleCloseWorkflow = (isWorkflowComplete: boolean) => {
    if (!isWorkflowComplete) {
      if (choiceValueForWorkflow) {
        const newResponse = new Set(response);
        newResponse.delete(choiceValueForWorkflow);
        updateResponse([...newResponse]);
      }
    }
    setWorkFlows([]);
    setChoiceValueForWorkflow(null);
  };

  const handleBothCloseWorkflow = (isWorkflowComplete: boolean) => {
    if (!isWorkflowComplete && workflows.length) {
      if (choiceValueForWorkflow) {
        const newResponse = new Set(response);
        newResponse.delete(choiceValueForWorkflow);
        updateResponse([...newResponse]);
      }
    }

    setWorkFlows([]);
    setChoiceValueForWorkflow(null);
    setLongHints([]);
  };

  const debouncedAutoCompleteAction = React.useMemo(
    () =>
      _.debounce((newSearchQuery: string) => {
        autoComplete.search(newSearchQuery);
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autoComplete.search],
  );

  const onAutoCompleteChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchQuery = event.target.value;
      setSearchQueryText(newSearchQuery);
      debouncedAutoCompleteAction(newSearchQuery);
    },
    [debouncedAutoCompleteAction],
  );

  return (
    <div className={classes.choicesContainer} ref={anchorRef}>
      {showSelectedChoices && (
        <div className={classes.preview}>
          {'Choice Paths: '}
          {groupSameLabelChoices
            ? getCategoryAnnotationCheckedGroupLabels(
                response,
                updateResponse,
                field,
                setNodesInPath,
                responseSetToNodesInPath,
                findAndSetLongHints,
                disabled,
              )
            : getCategoryAnnotationChecked(
                response,
                updateResponse,
                field,
                setNodesInPath,
                responseSetToNodesInPath,
                findAndSetLongHints,
                styleSelectedChoices,
                disabled,
              )}
        </div>
      )}
      {mobile && (
        <ChoiceLongHintAndWorkflow
          longHints={longHints}
          isOpen={!_.isEmpty(longHints) || (!_.isEmpty(workflows) && !!choiceValueForWorkflow)}
          anchorRef={anchorRef}
          choice={getChoiceFromValue(field.choices, choiceValueForWorkflow!)}
          workflows={workflows}
          onClose={handleBothCloseWorkflow}
        />
      )}
      {!mobile && (
        <>
          <ChoiceLongHint
            longHints={longHints}
            isOpen={!_.isEmpty(longHints)}
            anchorRef={anchorRef}
            previousLongHints={previousLongHints}
            childrenLongHints={childrenLongHints}
            setLongHints={setLongHints}
            setPreviousLongHints={setPreviousLongHints}
            setChildrenLongHints={setChildrenLongHints}
          />
          {!_.isEmpty(workflows) && !!choiceValueForWorkflow && (
            <ChoiceWorkflow
              choice={getChoiceFromValue(field.choices, choiceValueForWorkflow)}
              anchorRef={anchorRef}
              workflows={workflows}
              onClose={handleCloseWorkflow}
            />
          )}
        </>
      )}
      {searchEnabled && (
        <div className={classes.autoCompleteRow}>
          <TextInput
            value={searchQueryText}
            onChange={onAutoCompleteChange}
            fullWidth
            placeholder="Search choices..."
            onKeyDown={e => {
              if ((e.key === 'Escape' || e.key === 'Tab') && e.target instanceof HTMLElement) {
                e.target.blur();
              }
              e.stopPropagation();
            }}
          />
        </div>
      )}
      {showChoices &&
        autoComplete.choices.map((choice, idx) =>
          renderChoice(
            choice,
            isFocused && useKeyboardShortcuts ? ALPHABETIC_SHORTCUTS[idx] : undefined,
          ),
        )}
    </div>
  );
};

interface ChoiceLabelProps {
  label: string;
  hint?: string;
  subLabel?: string;
  preferred?: boolean;
  link?: string;
}

const ChoiceLabel: React.FC<ChoiceLabelProps> = props => {
  const classes = useCategorizationFieldSectionStyles();
  return (
    <div>
      <span className={classnames(classes.label, props.preferred ? classes.suggested : {})}>
        {props.label}
        {props.hint && <HintTooltipIcon hint={props.hint} />}
        {props.link && <HintTooltipIcon hint={props.link} link={true} />}
      </span>
      {props.subLabel && <div className={classes.subLabel}>{props.subLabel}</div>}
    </div>
  );
};

const ParentCheckbox: React.FC<{
  checked?: boolean;
  label: React.ReactNode;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  canSelectIntermediateNodes?: boolean;
  highlightNode?: boolean;
  shortcut?: string; // shortcut will only show up if focused
}> = props => {
  const { checked, label, onChange, disabled, canSelectIntermediateNodes, highlightNode } = props;

  const classes = useCategorizationFieldSectionStyles(canSelectIntermediateNodes);

  return (
    <div style={{ display: 'flex' }}>
      {props.shortcut && (
        <div className={classes.shortcut}>
          <Kbd>{props.shortcut}</Kbd>
        </div>
      )}
      <Checkbox
        className={classnames(
          canSelectIntermediateNodes ? classes.parentChoicesRow : classes.choicesRow,
          highlightNode ? classes.highlightNode : classes.unHighlightNode,
          { [classes.noPaddingLeft]: props.shortcut },
        )}
        classes={{ checkboxRoot: classes.checkboxCompact }}
        checked={checked}
        labelPlacement="end"
        label={label}
        onChange={onChange}
        icon={
          <ChevronRightIcon
            className={classnames({
              [classes.parentIconDisabled]: disabled,
            })}
            width="16"
            height="16"
          />
        }
        checkedIcon={
          <ChevronDownIcon
            width="16"
            height="16"
            className={classnames({
              [classes.parentIconExpanded]: !disabled,
              [classes.parentIconDisabled]: disabled,
            })}
          />
        }
        disabled={disabled}
        checkBoxColor="rgba(106, 93, 249)"
      />
    </div>
  );
};

ParentCheckbox.displayName = 'ParentCheckbox';

function isParentChoice(choice: CategoryChoice): boolean {
  return _.size(choice.subchoices) > 0;
}

export const useCategorizationFieldSectionStyles = makeStyles(theme => {
  return {
    title: {
      color: '#000000',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '20px',
      marginTop: '8px',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    helperText: {
      marginBottom: '16px',
    },
    preview: {
      fontSize: '14px',
      lineHeight: '18px',
      color: '#808080',
      margin: 8,
      fontWeight: 400,
    },
    filterOptions: {
      marginTop: '5px',
      marginBottom: '5px',
      fontWeight: 400,
    },
    responsePath: {
      color: '#000000',
    },
    first: {},
    last: {},
    choicesContainer: {
      border: `1px solid ${theme.colors.gray10}`,
      borderRadius: 8,
      padding: '8px 0',
      flexGrow: 1,
      maxHeight: '70vh',
      overflowY: 'scroll',
    },
    choiceContainer: {
      display: 'flex',
      flexDirection: 'row',
    },
    autoCompleteRow: {
      padding: '0 10px',
      marginBottom: 4,
    },

    choicesRow: {
      padding: '0 10px',
      flexGrow: 1,
      display: 'flex',
      '&:hover': {
        background: theme.colors.gray05,
      },
    },
    parentChoicesRow: {
      padding: 0,
      flexGrow: 1,
      display: 'flex',
      '&:hover': {
        background: theme.colors.gray05,
      },
    },
    parentRadio: {
      paddingLeft: 0,
    },
    suggested: {
      background: 'rgba(106, 93, 249, 0.25)',
      '&:hover': {
        background: 'rgba(106, 93, 249, 0.5)',
      },
    },
    checkboxLabel: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    highlightNode: {
      color: '#6A5DF9',
    },
    unHighlightNode: {
      color: 'black',
    },
    suggestedText: {
      color: 'rgba(106, 93, 249, 1)',
      display: 'flex',
      fontWeight: 500,
      marginRight: 16,
      flexDirection: 'row',
      alignItems: 'center',
    },
    checkboxCompact: (canSelectIntermediateNodes: boolean) => ({
      paddingRight: canSelectIntermediateNodes ? 6 : 'inherit',
    }),
    parentIconDisabled: {
      fill: 'rgba(0, 0, 0, 0.26)',
    },
    parentIconExpanded: {
      fill: '#6A5DF9',
    },
    subchoices: {
      marginLeft: 16,
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      lineHeight: '20px',
      whiteSpace: 'pre-wrap',
    },
    subLabel: {
      fontSize: '11px',
      color: theme.colors.gray50,
    },
    tooltipButton: {
      transform: 'translateY(2px)',
    },
    multiple: {
      marginBottom: 16,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    actions: {
      paddingTop: 8,
      paddingLeft: 8,
    },
    delete: {
      color: `rgba(0, 0, 0, 0.5)`,
      padding: 10,
      '& span': {
        padding: '0 1px',
      },
    },
    selectButton: {
      width: '100%',
    },
    addButton: {
      marginRight: 8,
      marginBottom: 8,
    },
    addButtonText: {
      color: '#6A5DF9',
      fontWeight: 600,
      fontSize: '13px',
      lineHeight: '20px',
      letterSpacing: '0.1px',
    },
    minMaxText: {
      color: theme.colors.black,
    },
    acceptableAuditStatusRow: {
      margin: 16,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontWeight: 500,
    },
    clickable: {
      cursor: 'pointer',
    },
    acceptableAuditStatusContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    acceptableAuditStatusItem: {
      marginLeft: 10,
    },
    acceptableAuditStatusMenuTitle: {
      margin: 8,
      marginBottom: 4,
    },
    shortcut: { display: 'flex', alignItems: 'center', marginLeft: 10 },
    noPaddingLeft: { paddingLeft: 0 },
  };
});

const TrashIcon = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M13.2 2.39961H10.2L9.96503 1.93211C9.91524 1.83217 9.83856 1.74809 9.74361 1.68935C9.64865 1.63061 9.53918 1.59953 9.42753 1.59961H6.57002C6.45862 1.59919 6.34935 1.63015 6.25473 1.68895C6.16011 1.74776 6.08396 1.83203 6.03502 1.93211L5.80002 2.39961H2.80002C2.69394 2.39961 2.5922 2.44176 2.51718 2.51677C2.44217 2.59179 2.40002 2.69353 2.40002 2.79961V3.59961C2.40002 3.7057 2.44217 3.80744 2.51718 3.88246C2.5922 3.95747 2.69394 3.99961 2.80002 3.99961H13.2C13.3061 3.99961 13.4079 3.95747 13.4829 3.88246C13.5579 3.80744 13.6 3.7057 13.6 3.59961V2.79961C13.6 2.69353 13.5579 2.59179 13.4829 2.51677C13.4079 2.44176 13.3061 2.39961 13.2 2.39961ZM3.73002 13.2746C3.7491 13.5793 3.88356 13.8652 4.10604 14.0742C4.32851 14.2832 4.62227 14.3996 4.92752 14.3996H11.0725C11.3778 14.3996 11.6715 14.2832 11.894 14.0742C12.1165 13.8652 12.2509 13.5793 12.27 13.2746L12.8 4.79961H3.20002L3.73002 13.2746Z" />
    </svg>
  );
};

function PlusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.8 6.80039H9.20003V3.20039C9.20003 2.75864 8.84177 2.40039 8.40002 2.40039H7.60002C7.15827 2.40039 6.80002 2.75864 6.80002 3.20039V6.80039H3.20002C2.75827 6.80039 2.40002 7.15864 2.40002 7.60039V8.40039C2.40002 8.84214 2.75827 9.20039 3.20002 9.20039H6.80002V12.8004C6.80002 13.2421 7.15827 13.6004 7.60002 13.6004H8.40002C8.84177 13.6004 9.20003 13.2421 9.20003 12.8004V9.20039H12.8C13.2418 9.20039 13.6 8.84214 13.6 8.40039V7.60039C13.6 7.15864 13.2418 6.80039 12.8 6.80039Z"
        fill="#2B99D8"
      />
    </svg>
  );
}

const ChevronDownIcon: React.FC<React.SVGAttributes<SVGElement>> = props => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M11.3636 16.7056L4.07572 9.41766C3.72423 9.06617 3.72423 8.49632 4.07572 8.14487L4.92573 7.29486C5.27662 6.94397 5.84531 6.94329 6.19702 7.29336L12 13.0691L17.8029 7.29336C18.1546 6.94329 18.7233 6.94397 19.0742 7.29486L19.9242 8.14487C20.2757 8.49636 20.2757 9.06621 19.9242 9.41766L12.6364 16.7056C12.2849 17.057 11.7151 17.057 11.3636 16.7056Z" />
    </svg>
  );
};

const ChevronRightIcon: React.FC<React.SVGAttributes<SVGElement>> = props => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M16.7053 12.6348L9.41741 19.9227C9.06593 20.2742 8.49608 20.2742 8.14463 19.9227L7.29461 19.0727C6.94373 18.7218 6.94305 18.1531 7.29311 17.8014L13.0689 11.9984L7.29311 6.19546C6.94305 5.84375 6.94373 5.27506 7.29461 4.92417L8.14463 4.07416C8.49611 3.72267 9.06596 3.72267 9.41741 4.07416L16.7053 11.362C17.0568 11.7135 17.0568 12.2833 16.7053 12.6348Z" />
    </svg>
  );
};

interface CategoryAutoComplete {
  isActive: boolean;
  pattern: string;
  choices: CategoryChoice[];
  search: (pattern: string) => void;
  clear(): void;
}

function useCategoryAutoComplete(
  field: CategoryField,
  excludeLeafNodes?: boolean,
  longHints?: Record<string, CategoryChoiceLongHint[]>,
  useSemanticSearch?: boolean,
  wordEmbeddings?: Record<string, number[]>,
  embeddingDim?: number,
): CategoryAutoComplete {
  const [pattern, setPattern] = React.useState('');

  const choiceNodes = React.useMemo(() => {
    return getChoiceNodesFromTree(field.choices, excludeLeafNodes, longHints);
  }, [field.choices, excludeLeafNodes, longHints]);

  const searchIndex = React.useMemo(() => {
    return buildCategoryChoiceSearchIndex(choiceNodes);
  }, [choiceNodes]);

  const keyVectors = React.useMemo(() => {
    if (useSemanticSearch) {
      return computeKeyVectors(choiceNodes, wordEmbeddings, embeddingDim);
    } else {
      return {};
    }
  }, [useSemanticSearch, choiceNodes, wordEmbeddings, embeddingDim]);

  const choices = React.useMemo(() => {
    if (!pattern) {
      return field.choices;
    }

    const fuseScores: Record<string, number> = {};
    if (field.tokenizeSearch) {
      const terms = _.compact(pattern.split(' '));

      for (const term of terms) {
        const searchResults = searchIndex.search(term);
        for (const searchResult of searchResults) {
          const keyName = searchResult.item.choice.label;
          if (keyName in fuseScores) {
            fuseScores[keyName] += 1 - (searchResult.score ?? 1);
          } else {
            fuseScores[keyName] = 1 - (searchResult.score ?? 1);
          }
        }
      }
    } else {
      const fuseResults = searchIndex.search(pattern);
      for (const fuseResult of fuseResults) {
        const keyName = fuseResult.item.choice.label;
        fuseScores[keyName] = 1 - (fuseResult.score ?? 1);
      }
    }

    // Rescore these to match the range of semantic search scores
    const scoredResults: Record<string, number> = {};
    for (const keyName in fuseScores) {
      const fuseScore = fuseScores[keyName];
      // Generally < 0.99 is fuzzy and > 0.99 is exact match
      if (fuseScore < 0.99) {
        // always < semantic results
        scoredResults[keyName] = -2 + fuseScore;
      } else {
        // always > semantic results
        scoredResults[keyName] = 1 + fuseScore;
      }
    }

    if (useSemanticSearch && wordEmbeddings && embeddingDim) {
      const semanticResults = semanticSearch(pattern, wordEmbeddings, keyVectors, embeddingDim);
      for (const key in semanticResults) {
        const semanticScore = semanticResults[key];
        if (semanticScore < 0.5) {
          continue;
        }
        if (key in scoredResults) {
          scoredResults[key] += semanticScore;
        } else {
          scoredResults[key] = semanticScore;
        }
      }
    }

    const matchedNodes = choiceNodes.filter(
      node => node.choice.label in scoredResults || node.choice.pinned,
    );
    const sortedChoices = [...matchedNodes].sort((choice1, choice2) => {
      // show pinned choices at the top of the list
      if (choice1.choice.pinned) {
        return -1;
      } else if (choice2.choice.pinned) {
        return 1;
      }
      const key1 = choice1.choice.label;
      const key2 = choice2.choice.label;
      return scoredResults[key2] - scoredResults[key1];
    });

    for (const choice of sortedChoices) {
      choice.choice.isFuseMatch = choice.choice.label in fuseScores;
    }

    if (excludeLeafNodes) {
      return _.uniq(_.compact(_.take(sortedChoices, 18).map(result => result.parent)));
    }
    return _.take(sortedChoices, 18).map(result => result.choice);
  }, [
    pattern,
    field.tokenizeSearch,
    field.choices,
    useSemanticSearch,
    wordEmbeddings,
    embeddingDim,
    choiceNodes,
    excludeLeafNodes,
    searchIndex,
    keyVectors,
  ]);

  const clear = () => {
    setPattern('');
  };

  return {
    isActive: !!pattern,
    pattern,
    choices,
    search: setPattern,
    clear,
  };
}

function getChoiceNodesFromTree(
  choices: CategoryChoice[],
  excludeLeafNodes?: boolean,
  longHints?: Record<string, CategoryChoiceLongHint[]>,
): ChoiceSearchNode[] {
  const choicesWithParents: ChoiceSearchNode[] = [];
  const parentChoices: CategoryChoice[] = [];
  let lastDepth = -1;
  for (const [choice, depth] of depthFirstChoices(choices)) {
    const drop = 1 + lastDepth - depth;
    parentChoices.splice(parentChoices.length - drop, drop);
    const parent = parentChoices.length > 0 ? parentChoices[parentChoices.length - 1] : undefined;
    if (!excludeLeafNodes || _.isEmpty(choice.subchoices)) {
      const choiceLongHints = longHints?.[choice.value as string];
      const choiceLongHintText = choiceLongHints
        ?.map(lh => lh.text ?? ' ')
        .join(' ')
        .trim();
      const choiceWorkflowText = choiceLongHints
        ?.map(lh => lh.workflow ?? ' ')
        .join(' ')
        .trim();
      choicesWithParents.push({ choice, parent, choiceLongHintText, choiceWorkflowText });
    }
    parentChoices.push(choice);
    lastDepth = depth;
  }
  return choicesWithParents;
}

function buildCategoryChoiceSearchIndex(choiceNodes: ChoiceSearchNode[]): Fuse<ChoiceSearchNode> {
  const searchIndex = new Fuse(choiceNodes, {
    keys: [
      { name: 'choice.label', weight: 1 },
      { name: 'choice.hint', weight: 0.5 },
      { name: 'choiceLongHintText', weight: 1 },
      { name: 'choiceWorkflowText', weight: 0.3 },
    ],
    threshold: 0.4,
    ignoreLocation: true,
    ignoreFieldNorm: true,
    includeScore: true,
  });

  return searchIndex;
}

function computeKeyVectors(
  choiceNodes: ChoiceSearchNode[],
  wordEmbeddings?: Record<string, number[]>,
  embeddingDim?: number,
): Record<string, number[]> {
  if (wordEmbeddings === undefined || embeddingDim === undefined) {
    return {};
  }

  const keyVectors: Record<string, number[]> = {};

  for (const choice of choiceNodes) {
    const keyName = choice.choice.label;
    keyVectors[keyName] = embedString(keyName, wordEmbeddings, embeddingDim);
  }

  return keyVectors;
}

function semanticSearch(
  query: string,
  wordEmbeddings: Record<string, number[]>,
  keyVectors: Record<string, number[]>,
  embeddingDim: number,
): Record<string, number> {
  if (!isInVocabulary(query, wordEmbeddings)) {
    return {};
  }

  const queryVector = embedString(query, wordEmbeddings, embeddingDim);
  const similarityScores: Record<string, number> = {};

  for (const key in keyVectors) {
    const similarityScore = cosineSimilarity(queryVector, keyVectors[key]);
    similarityScores[key] = similarityScore;
  }

  return similarityScores;
}

interface ChoiceSearchNode {
  choice: CategoryChoice;
  parent?: CategoryChoice;
  choiceLongHintText?: string;
  choiceWorkflowText?: string;
}
